import React, { useState } from 'react';
import {
    Box, Button, TextField, Typography,
} from '@mui/material';
import useStyles from '../../../assets/styles/DqAiChatbotPreviewStyles';
import { useDqAiChatbot } from './hooks/useDqAiChatbot';
import DqAiChatPanel from './components/DqAiChatPanel.comp';

/**
 * DQ AI chatbot preview (/chatbot_preview) — login-free page for research-team
 * quality testing. Chat on the left, live collected-data panel on the right
 * (mirrors the notebook UI: session code, resume-by-code, ◐ pending marks).
 */

const StatusPanel = ({ classes, collectedView, done }) => {
    if (!collectedView) return null;
    const badgeDone = done || collectedView.build_confirmed;
    return (
        <Box className={classes.statusColumn}>
            <Typography style={{ fontWeight: 700, fontSize: 13 }}>📋 Collected data</Typography>
            <table className={classes.statusTable}>
                <tbody>
                    {collectedView.rows.map((row) => (
                        <tr key={row.key}>
                            <td className={classes.statusLabel}>{row.label}</td>
                            {row.value == null ? (
                                <td className={classes.statusEmpty}>Not collected</td>
                            ) : (
                                <td className={row.pending ? classes.statusPending : classes.statusValue}>
                                    {row.pending ? `◐ ${row.value} (pending confirmation)` : `✅ ${row.value}`}
                                </td>
                            )}
                        </tr>
                    ))}
                    <tr>
                        <td className={classes.statusLabel}>Reply language</td>
                        <td className={classes.statusValue}>{collectedView.reply_language}</td>
                    </tr>
                    <tr>
                        <td className={classes.statusLabel}>Unclear streak</td>
                        <td className={classes.statusValue}>{`${collectedView.unclear_count} / 3`}</td>
                    </tr>
                </tbody>
            </table>
            <Box className={`${classes.statusBadge} ${badgeDone ? classes.badgeDone : classes.badgeCollecting}`}>
                {badgeDone ? 'Build confirmed — payload ready' : 'Collecting'}
            </Box>
        </Box>
    );
};

const DqAiChatbotPreviewPage = () => {
    const classes = useStyles();
    const [resumeCode, setResumeCode] = useState('');
    const {
        sessionId,
        chatHistory,
        collectedView,
        done,
        isSending,
        errorText,
        startNewSession,
        resumeSession,
        sendMessage,
    } = useDqAiChatbot();

    // The page always opens blank — a session (and its unique code) is created only
    // when the tester clicks "New chat" or resumes an existing code.
    const handleResume = async () => {
        const ok = await resumeSession(resumeCode);
        if (ok) setResumeCode('');
    };

    return (
        <Box className={classes.pageWrapper}>
            <Box className={classes.header}>
                <Box className={classes.headerTitleWrap}>
                    <Typography style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>
                        DQ AI — Chatbot Preview
                    </Typography>
                    <Typography className={classes.sessionCode}>
                        {sessionId ? `Session: ${sessionId}` : 'No session — press New chat to start'}
                    </Typography>
                </Box>
                <Box className={classes.headerRight}>
                    <TextField
                        size="small"
                        placeholder="Paste session code to resume…"
                        value={resumeCode}
                        onChange={(e) => setResumeCode(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleResume(); }}
                        disabled={isSending}
                        sx={{ width: 240 }}
                    />
                    {/* Pill buttons in the product chatbot's orange accent
                        (DQProV2 ChatSidebar style) */}
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={isSending || !resumeCode.trim()}
                        onClick={handleResume}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '20px',
                            color: '#FF6900',
                            borderColor: '#FF6900',
                            '&:hover': { borderColor: '#E65C00', backgroundColor: '#FEF0EC' },
                        }}
                    >
                        Resume
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={isSending}
                        onClick={startNewSession}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '20px',
                            color: '#fff',
                            background: 'linear-gradient(135deg, #FF6900 0%, #FF8C33 100%)',
                            boxShadow: 'none',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #E65C00 0%, #FF7A1A 100%)',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        🔄 New chat
                    </Button>
                </Box>
            </Box>

            {errorText && (
                <Box sx={{ px: 2, py: 0.5, color: '#b00020', fontSize: 13 }}>{errorText}</Box>
            )}

            <Box className={classes.mainRow}>
                <Box className={classes.chatColumn}>
                    <DqAiChatPanel
                        classes={classes}
                        chatHistory={chatHistory}
                        isSending={isSending}
                        hasSession={Boolean(sessionId)}
                        onSendMessage={sendMessage}
                    />
                </Box>
                <StatusPanel classes={classes} collectedView={collectedView} done={done} />
            </Box>
        </Box>
    );
};

export default DqAiChatbotPreviewPage;
